import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import axios from 'axios';
import allSettled from 'promise.allsettled';

import { analytics } from 'firebaseConfig';

import {
  observeUser,
  updateProfile,
  contact,
  signOutRequest,
  sendFeedback,
  onboardingUpdate,
  fetchCurrentIntegrationsRequest,
  signInSuccess,
} from 'modules/Users/store/actions';
import { cancelTeamsListener, teamsRequest } from 'modules/Team/store/actions';
import { addWebPage } from 'modules/Web/store/actions';

import Loading from 'components/Loading';

import App from '../components/App';
import MobileVersion from '../components/MobileVersion';
import SWUnsupported from '../components/SWUnsupported';

import {
  showSnackbar,
  hideSnackbar,
  updateSearchParams,
  toggleDemoModal,
  toggleProvideFeedbackModal,
  toggleSubmitFeedback,
  contactUpdate,
  inspectExitCallback as setInspectExitCallback,
  inspectedChangesCount,
  toggleVideoModal,
  toggleTrelloGuide,
  toggleSimilarPagesModal,
  toggleAsanaGuide,
} from '../store/actions';

const AppContainer = ({
  dispatch,
  user: currentUser,
  projects,
  appLoading,
  showDemoModal,
  showProvideFeedbackModal,
  contactUs,
  inspectExitCallback,
  inspectChangesCount,
  reauth,
  videoModal,
  showTrelloGuide,
  showAsanaGuide,
  teams,
  similarPages,
  pages,
  ...rest
}) => {
  const [finishUserSetup, setFinishUserSetup] = useState(false);
  const [isMobileScreen, setMobileScreen] = useState(false);
  const [isSWSupported, setSWSupported] = useState(true);

  const lastLocation = useRef();

  const navigate = useNavigate();
  const location = useLocation();

  const offlineHandler = useCallback(() => {
    dispatch(
      showSnackbar(
        'You are offline. Reconnect to the internet and refresh the page.',
      ),
    );
  }, [dispatch]);

  const onlineHandler = useCallback(() => {
    dispatch(showSnackbar("We're back online!"));
    setTimeout(() => dispatch(hideSnackbar()), 5000);
  }, [dispatch]);

  const resizeHandler = useCallback(() => {
    setMobileScreen(window.innerWidth < 979);
  }, []);

  const closeSnackbar = useCallback(() => {
    dispatch(hideSnackbar());
  }, [dispatch]);

  const updateProfileDispatch = useCallback(
    (payload) => {
      dispatch(
        updateProfile(payload, () => {
          dispatch(cancelTeamsListener());

          dispatch(teamsRequest());
        }),
      );
      setFinishUserSetup(false);
    },
    [dispatch],
  );

  const closeDemoModal = useCallback(() => {
    dispatch(toggleDemoModal(false));
  }, [dispatch]);

  const closeProvideFeedbackModal = useCallback(() => {
    dispatch(toggleProvideFeedbackModal(false));
  }, [dispatch]);

  const openSubmitFeedback = useCallback(() => {
    dispatch(toggleSubmitFeedback(true));
  }, [dispatch]);

  const closeContactModal = useCallback(() => {
    dispatch(contactUpdate({ showContactModal: false, success: false }));
  }, [dispatch]);

  const submitContact = useCallback(
    (payload) => {
      dispatch(contact(payload));
    },
    [dispatch],
  );

  const confirmInspectSaveExit = useCallback(() => {
    dispatch(inspectedChangesCount(-2));
    setTimeout(() => {
      if (inspectExitCallback && inspectExitCallback.fn) {
        inspectExitCallback.fn(...inspectExitCallback.args);
        dispatch(setInspectExitCallback(null));
      }
    }, [500]);
  }, [dispatch, inspectExitCallback]);

  const confirmInspectExit = useCallback(() => {
    dispatch(inspectedChangesCount(-1));
    if (inspectExitCallback && inspectExitCallback.fn) {
      inspectExitCallback.fn(...inspectExitCallback.args);
      dispatch(setInspectExitCallback(null));
    }
  }, [dispatch, inspectExitCallback]);

  const confirmInspectStay = useCallback(() => {
    dispatch(setInspectExitCallback(null));
  }, [dispatch]);

  const isAuthenticatedRoute = useCallback(() => {
    const { pathname } = location;

    return (
      pathname !== '/' &&
      pathname !== '/forgot-password' &&
      pathname !== '/user-action' &&
      pathname !== '/signup' &&
      !pathname.startsWith('/share') &&
      !pathname.startsWith('/preview')
    );
  }, [location]);

  const signOut = useCallback(() => {
    dispatch(signOutRequest({}, navigate));
  }, [dispatch, navigate]);

  const feedbackSubmit = useCallback(
    (payload) => {
      dispatch(
        sendFeedback({
          ...payload,
          userEmail: currentUser.email,
          userID: currentUser.uid,
          url: `${window.location.origin}${location.pathname}${location.search}`,
        }),
      );
    },
    [
      currentUser.email,
      currentUser.uid,
      dispatch,
      location.pathname,
      location.search,
    ],
  );

  const hideVideoModal = useCallback(() => {
    dispatch(
      toggleVideoModal({
        showVideoModal: false,
        source: '',
      }),
    );
  }, [dispatch]);

  const closeTrelloGuide = useCallback(() => {
    dispatch(toggleTrelloGuide(false));
  }, [dispatch]);

  const closeAsanaGuide = useCallback(() => {
    dispatch(toggleAsanaGuide(false));
  }, [dispatch]);

  const errorHandler = useCallback((e) => {
    if (/Loading chunk [\d]+ failed/.test(e.message)) window.location.reload();
  }, []);

  const resetSimilarPages = useCallback(() => {
    dispatch(
      toggleSimilarPagesModal({
        show: false,
        pages: [],
        url: '',
      }),
    );
  }, [dispatch]);

  const addSimilarPagesDispatch = useCallback(
    async (payload) => {
      resetSimilarPages();

      let promises = payload.map(
        ({ href: url, isScreenshot }) =>
          new Promise((resolve, reject) => {
            (async () => {
              try {
                const res = await axios.get(`/fetcher/${url}`, {
                  headers: { accept: 'text/html' },
                });

                const { status, data } = res;

                if (status === 200) {
                  if (data.length) {
                    resolve({
                      url: res.headers['x-final-url'] || url,
                      data: res.data,
                      isScreenshot,
                    });
                  } else {
                    reject(
                      new Error(
                        "Error: We weren't able to get any data from the site.",
                      ),
                    );
                  }
                } else {
                  reject(status);
                }
              } catch (e) {
                reject(e);
              }
            })();
          }),
      );

      promises = ((await allSettled(promises)) || []).filter(
        ({ status }) => status === 'fulfilled',
      );

      if (promises.length > 0) {
        promises.forEach(({ value }, index) => {
          dispatch(
            addWebPage({
              ...value,
              paginate: { index: index + 1, total: promises.length },
            }),
          );
        });
      }
    },
    [dispatch, resetSimilarPages],
  );

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      '%c Made with ♥ by ruttl (https://ruttl.com).',
      'margin: 1em 0; padding: 0.5rem; color: rgb(255, 255, 255); background-color: rgb(101, 82, 255);',
    );

    if ('serviceWorker' in navigator) {
      if (window.ruler?.install) window.ruler.install();
    } else setSWSupported(false);
  }, [dispatch]);

  useEffect(() => {
    dispatch(observeUser(navigate));
  }, [dispatch, navigate]);

  useEffect(() => {
    if (location.search) dispatch(updateSearchParams(location.search));
  }, [location, dispatch]);

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length > 0) {
      if (
        currentUser.emailVerified === false &&
        !location.pathname.startsWith('/user-action') &&
        !location.pathname.startsWith('/signup')
      )
        navigate('/signup');
      else if (
        (!currentUser.displayName || !currentUser.photoURL) &&
        currentUser.emailVerified === true
      )
        setFinishUserSetup(true);

      if (reauth && location.pathname !== '/') navigate('/?delete=true');
    }
  }, [currentUser, navigate, location, reauth]);

  useEffect(() => {
    if (lastLocation.current !== `${location.pathname}${location.search}`) {
      logEvent(analytics, 'route_change', {
        event_user: (currentUser && currentUser.uid) || null,
        path: `${location.pathname}${location.search}`,
      });
      lastLocation.current = `${location.pathname}${location.search}`;
    }
  }, [location, currentUser]);

  useEffect(() => {
    let clear = null;

    if (
      Object.keys(currentUser).length > 0 &&
      typeof currentUser.projectsCount !== 'undefined' &&
      projects?.length > 1 &&
      finishUserSetup === false &&
      currentUser.onboarding?.provideFeedbackWT === true &&
      !currentUser.onboarding?.provideFeedbackModal &&
      projects.reduce((a, c) => (c.owner === currentUser.uid ? a + 1 : a), 0) >=
        2
    ) {
      clear = setTimeout(() => {
        dispatch(toggleProvideFeedbackModal(true));
        dispatch(onboardingUpdate('provideFeedbackModal'));
      }, 10000);
    }

    return () => (clear ? clearTimeout(clear) : null);
  }, [dispatch, currentUser, projects, finishUserSetup]);

  useEffect(() => {
    // Confirm currentUser permissions if team active exists.

    if (
      currentUser &&
      typeof currentUser.projectsCount !== 'undefined' &&
      typeof currentUser.projectPagesCount !== 'undefined' &&
      typeof currentUser.projectShareCount !== 'undefined' &&
      typeof currentUser.projectEditorsCount !== 'undefined' &&
      teams?.length > 0 &&
      teams.some(({ isDisabled }) => isDisabled === false) &&
      (currentUser.projectsCount !== -1 ||
        currentUser.projectPagesCount !== -1 ||
        currentUser.projectShareCount !== -1 ||
        currentUser.projectEditorsCount !== -1)
    ) {
      dispatch(
        signInSuccess({
          projectsCount: -1,
          projectPagesCount: -1,
          projectShareCount: -1,
          projectEditorsCount: -1,
        }),
      );
      /* captureMessage(
        `Error: Permissions of UID ${currentUser.uid} are not correct in user document, but is an active team member.`,
      ); */
    }
  }, [dispatch, teams, currentUser]);

  /* useEffect(() => {
    axios
      .get(
        'chrome-extension://doecfodblgfnjmmphbedjgllipnbgbld/images/ruttl-icon-16.png',
      )
      .then(() => {
        dispatch(chromeExtensionActive(true));
      })
      .catch(() => {
        dispatch(chromeExtensionActive(false));
      });
  }, [dispatch]); */

  useEffect(() => {
    window.addEventListener('offline', offlineHandler);
    window.addEventListener('online', onlineHandler);

    return () => {
      window.removeEventListener('offline', offlineHandler);
      window.removeEventListener('online', onlineHandler);
    };
  }, [offlineHandler, onlineHandler]);

  useEffect(() => {
    resizeHandler();
    window.addEventListener('resize', resizeHandler);

    return () => window.removeEventListener('resize', resizeHandler);
  }, [resizeHandler]);

  useEffect(() => {
    window.addEventListener('error', errorHandler);

    return () => window.removeEventListener('error', errorHandler);
  }, [errorHandler]);

  useEffect(() => {
    if (currentUser.uid) {
      dispatch(fetchCurrentIntegrationsRequest());
    }
  }, [dispatch, currentUser]);

  return appLoading ? (
    <Loading />
  ) : (
    <>
      {isSWSupported ? (
        <>
          {isAuthenticatedRoute() && isMobileScreen ? (
            <MobileVersion signOut={signOut} />
          ) : (
            <App
              {...rest}
              closeSnackbar={closeSnackbar}
              currentUser={currentUser}
              finishUserSetup={
                location.pathname === '/' ||
                location.pathname === '/signup' ||
                location.pathname.startsWith('/user-action')
                  ? false
                  : finishUserSetup
              }
              updateProfile={updateProfileDispatch}
              showDemoModal={showDemoModal}
              showProvideFeedbackModal={showProvideFeedbackModal}
              contactUs={contactUs}
              closeDemoModal={closeDemoModal}
              closeProvideFeedbackModal={closeProvideFeedbackModal}
              closeContactModal={closeContactModal}
              submitContact={submitContact}
              openSubmitFeedback={openSubmitFeedback}
              inspectExitCallback={inspectExitCallback}
              inspectChangesCount={inspectChangesCount}
              confirmInspectExit={confirmInspectExit}
              confirmInspectStay={confirmInspectStay}
              confirmInspectSaveExit={confirmInspectSaveExit}
              feedbackSubmit={feedbackSubmit}
              videoModal={videoModal}
              hideVideoModal={hideVideoModal}
              showTrelloGuide={showTrelloGuide}
              closeTrelloGuide={closeTrelloGuide}
              showAsanaGuide={showAsanaGuide}
              closeAsanaGuide={closeAsanaGuide}
              similarPages={similarPages}
              resetSimilarPages={resetSimilarPages}
              addSimilarPages={addSimilarPagesDispatch}
              pages={pages}
            />
          )}
        </>
      ) : (
        <SWUnsupported />
      )}
    </>
  );
};

const mapStateToProps = ({
  app: {
    snackbar,
    appLoading,
    showDemoModal,
    showProvideFeedbackModal,
    showSubmitFeedback,
    contact: contactUs,
    inspectExitCallback,
    inspectChangesCount,
    video,
    showTrelloGuide,
    showAsanaGuide,
    similarPages,
  },
  user: { user, reauth },
  project: { projects },
  web: { pages },
  team: { teams },
}) => ({
  snackbar,
  user,
  projects,
  appLoading,
  showDemoModal,
  showProvideFeedbackModal,
  showSubmitFeedback,
  contactUs,
  inspectExitCallback,
  inspectChangesCount,
  reauth,
  videoModal: video,
  showTrelloGuide,
  showAsanaGuide,
  teams,
  similarPages,
  pages,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

AppContainer.propTypes = {
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  projects: PropTypes.array,
  appLoading: PropTypes.bool.isRequired,
  showDemoModal: PropTypes.bool.isRequired,
  showProvideFeedbackModal: PropTypes.bool.isRequired,
  contactUs: PropTypes.object.isRequired,
  inspectExitCallback: PropTypes.object,
  inspectChangesCount: PropTypes.number.isRequired,
  reauth: PropTypes.bool.isRequired,
  videoModal: PropTypes.object.isRequired,
  showTrelloGuide: PropTypes.bool.isRequired,
  showAsanaGuide: PropTypes.bool.isRequired,
  teams: PropTypes.array,
  similarPages: PropTypes.object.isRequired,
  pages: PropTypes.array.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
